// packages/ui-kit/src/primitives/StatCard.tsx
// C3F v18 — reference: design/system/components/core/StatCard.{jsx,prompt.md}
// Single number with a mono label — the Monitoring dashboard is made of these.
// `dark` is not an aesthetic: the Night card is Night in BOTH themes, so it
// rides Card tone="night" (which carries `surface-night`) and the inner
// Spectrum bar + muted inks re-resolve from that subtree with no extra props.
import { type ReactNode } from "react";
import { Card } from "./Card.js";

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

interface Props {
  label: string;
  value: string | number;
  unit?: string;
  tone?: Tone;
  description?: ReactNode;
  /** 0-100: draws the Spectrum bar under the number. Main view progress ONLY —
   *  the Spectrum gradient is a signature, not a chart color. */
  progress?: number;
  dark?: boolean;
  className?: string;
}

// Inside `surface-night` these SAME classes resolve to the always-dark values,
// so one map serves both modes (neutral = --fg-default, which IS paper there).
const valueCls: Record<Tone, string> = {
  neutral: "text-fg-default",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};

export function StatCard({
  label,
  value,
  unit,
  tone = "neutral",
  description,
  progress,
  dark = false,
  className = "",
}: Props) {
  // On the always-Night card the muted ink is --sidebar-muted (measured on a
  // dark ground); --fg-muted is a THEME token and would follow Paper (DS note).
  const mutedCls = dark ? "text-sidebar-muted" : "text-fg-muted";
  const v = typeof progress === "number" ? Math.max(0, Math.min(100, progress)) : null;
  return (
    <Card tone={dark ? "night" : "subtle"} padding="md" className={`grid gap-2.5 ${className}`}>
      <div className={`text-eyebrow ${dark ? "text-sidebar-muted" : ""}`}>{label}</div>
      <div className="flex items-baseline gap-2">
        {/* Numbers are reading units: mono, bold, and they NEVER wrap. The
            34px step is the DS reference's own display size for this card. */}
        <span
          className={`whitespace-nowrap font-mono text-[34px] font-bold leading-none tracking-[-0.03em] ${valueCls[tone]}`}
        >
          {value}
        </span>
        {unit && <span className={`text-sm ${mutedCls}`}>{unit}</span>}
      </div>
      {v !== null && (
        <div className="h-1.5 w-full overflow-hidden rounded-pill bg-hover">
          <div
            className="h-full rounded-pill bg-[image:var(--spectrum-8h)] transition-[width] duration-slow"
            // Truly dynamic values: the fill width and the gradient stretch
            // (full Spectrum across the track regardless of fill width).
            style={{ width: `${v}%`, backgroundSize: `${10000 / Math.max(v, 1)}% 100%` }}
          />
        </div>
      )}
      {description && <div className={`text-caption ${mutedCls}`}>{description}</div>}
    </Card>
  );
}
